import { classes } from 'html-classes';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CarouselIcon } from '~/components/CommonCarousel';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { Category } from '~/stores/CategoriesStore';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { cfSrcForImage, createCategoryUrl } from '../utils';

import styles from './CategoriesCarouselSliderTwoSBlock.module.scss';

interface CategoriesCarouselSliderTwoSBlockProps {
  category: Category;
}

const CategoriesCarouselSliderTwoSBlock = ({
  category,
}: CategoriesCarouselSliderTwoSBlockProps) => {
  const { handleClickCategory } = useCategoryAnalytics();
  const { isMobile } = useGlobal();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  if (!category.subcategory.length || !category.subcategory[0]?.id) {
    return null;
  }

  const items = category.subcategory.map(
    ({ previewImage, name, previewText, id }) => ({
      id,
      node: (
        <div
          className={styles.whatToChooseElement}
          style={{
            backgroundImage: `url('${cfSrcForImage(previewImage, {
              width: 224,
              height: 320,
            })}')`,
          }}
        >
          <Link
            onClick={handleClickCategory(id)}
            to={createCategoryUrl({ id })}
            className={styles.link}
          >
            <div className={styles.whatToChooseTitle}>{name}</div>
            <div
              className={styles.whatToChoosePrice}
              dangerouslySetInnerHTML={{ __html: previewText }}
            />
          </Link>
        </div>
      ),
    }),
  );

  const showArrows = !isMobile && items.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.info}>
          <CategoryBlockTitle className={styles.title} text={category.name} />
          <div
            className={styles.cta}
            dangerouslySetInnerHTML={{ __html: category.previewText }}
          />
        </div>

        <div className={styles.carouselWrapper}>
          {showArrows && (
            <CarouselIcon
              position="left"
              variant="fill"
              className={classes([
                styles.carouselPrevButton,
                prevBtnDisabled && styles.hidden,
              ])}
              onClick={onPrevButtonClick}
            />
          )}
          <div className={styles.carousel}>
            <Carousel
              opts={{
                align: 'start',
                slidesToScroll: isMobile ? 3 : 4,
                containScroll: 'trimSnaps',
                active: items.length > (isMobile ? 3 : 4),
              }}
              setApi={setApi}
            >
              <CarouselContent className={styles.carouselContent}>
                {items.map((item) => (
                  <CarouselItem key={item.id} className={styles.slide}>
                    {item.node}
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
          {showArrows && (
            <CarouselIcon
              position="right"
              variant="fill"
              className={classes([
                styles.carouselNextButton,
                nextBtnDisabled && styles.hidden,
              ])}
              onClick={onNextButtonClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesCarouselSliderTwoSBlock;
